import React, { useState, useEffect } from "react";
import PlayerStats from "./PlayerStats";
import PartyWidget from "./PartyWidget";
import BoxWidget from "./BoxWidget";
import ManageItemsWidget from "./ManageItemsWidget";
import NotificationsWidget from "./NotificationsWidget";
import RadiantIsleWidget from "./RadiantIsleWidget";
import WeeklyTask from "./WeeklyTask";
import LastActiveUsers from "./LastActiveUsers";
import { Row, Alert } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useRecoilValue } from "recoil";
import { newState, bannedState } from "../../Recoil/atom";
import api from "../../api";
import NewUserModal from "../../components/Common/NewUserModal";

// Error Boundary Component for individual dashboard sections
const ErrorBoundary = ({ children, fallback, componentName }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const handleError = (error) => {
      console.error(`Error in ${componentName}:`, error);
      setHasError(true);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, [componentName]);

  if (hasError) {
    return fallback || (
      <Alert color="warning" className="mb-3">
        <h6>Unable to load {componentName}</h6>
        <small className="text-muted">This section is temporarily unavailable. Please refresh the page or try again later.</small>
      </Alert>
    );
  }

  return children;
};

// Safe component wrapper that catches render errors
const SafeComponent = ({ component: Component, fallback, name, ...props }) => {
  try {
    return (
      <ErrorBoundary componentName={name} fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    );
  } catch (error) {
    console.error(`Render error in ${name}:`, error);
    return fallback || (
      <Alert color="warning" className="mb-3">
        <h6>Unable to load {name}</h6>
        <small className="text-muted">This section is temporarily unavailable.</small>
      </Alert>
    );
  }
};

const Dashboard = () => {
  document.title = "Dashboard | Pokémon Radiance";
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Dashboard widget settings from backend API
  const [widgetSettings, setWidgetSettings] = useState({
    playerStats: true,
    party: false,
    box: false,
    radiantIsle: true,
    manageItems: false,
    notifications: false,
    weeklyMissions: true,
    lastActive: true
  });

  // Widget order from backend API
  const [widgetOrder, setWidgetOrder] = useState([
    'playerStats',
    'radiantIsle',
    'party',
    'box',
    'weeklyMissions',
    'lastActive',
    'manageItems',
    'notifications'
  ]);

  // Widget colors from backend API
  const [widgetColors, setWidgetColors] = useState(null);

  // Loading state for widget settings
  const [widgetSettingsLoading, setWidgetSettingsLoading] = useState(true);

  // Widget component mapping
  const widgetComponents = {
    playerStats: {
      component: PlayerStats,
      name: "Player Stats",
      fallback: (
        <Alert color="info" className="mb-3">
          <h6>Player Stats Unavailable</h6>
          <small>Your profile information is temporarily unavailable.</small>
        </Alert>
      )
    },
    party: {
      component: PartyWidget,
      name: "Party Widget",
      fallback: (
        <Alert color="info" className="mb-3">
          <h6>Party Unavailable</h6>
          <small>Your party information is temporarily unavailable.</small>
        </Alert>
      )
    },
    box: {
      component: BoxWidget,
      name: "Box Widget",
      fallback: (
        <Alert color="info" className="mb-3">
          <h6>Box Unavailable</h6>
          <small>Your Pokémon box is temporarily unavailable.</small>
        </Alert>
      )
    },
    manageItems: {
      component: ManageItemsWidget,
      name: "Manage Items",
      fallback: (
        <Alert color="info" className="mb-3">
          <h6>Items Unavailable</h6>
          <small>Your items are temporarily unavailable.</small>
        </Alert>
      )
    },
    notifications: {
      component: NotificationsWidget,
      name: "Notifications",
      fallback: (
        <Alert color="info" className="mb-3">
          <h6>Notifications Unavailable</h6>
          <small>Notifications are temporarily unavailable.</small>
        </Alert>
      )
    },
    radiantIsle: {
      component: RadiantIsleWidget,
      name: "Radiant Isle",
      fallback: (
        <Alert color="info" className="mb-3">
          <h6>Radiant Isle Unavailable</h6>
          <small>The Radiant Isle is temporarily unavailable.</small>
        </Alert>
      )
    },
    weeklyMissions: {
      component: WeeklyTask,
      name: "Weekly Tasks",
      fallback: (
        <div className="col-md-6">
          <Alert color="info" className="mb-3">
            <h6>Weekly Tasks Unavailable</h6>
            <small>Weekly tasks are temporarily unavailable.</small>
          </Alert>
        </div>
      )
    },
    lastActive: {
      component: LastActiveUsers,
      name: "Last Active Users",
      fallback: (
        <div className="col-md-6">
          <Alert color="info" className="mb-3">
            <h6>Active Users Unavailable</h6>
            <small>Active users list is temporarily unavailable.</small>
          </Alert>
        </div>
      )
    }
  };

  // Function to render widget based on order
  const renderWidget = (widgetKey) => {
    if (!widgetSettings[widgetKey] || !widgetComponents[widgetKey]) {
      return null;
    }

    const { component, name, fallback } = widgetComponents[widgetKey];
    
    // Special handling for banned users - skip certain widgets
    if (isBanned && isBanned.banned && (widgetKey === 'weeklyMissions' || widgetKey === 'lastActive')) {
      return null;
    }

    return (
      <SafeComponent 
        key={widgetKey}
        component={component} 
        name={name}
        fallback={fallback}
      />
    );
  };

  // Always call hooks at the top level - handle errors in useEffect
  const recoilNewUser = useRecoilValue(newState);
  const recoilBanned = useRecoilValue(bannedState);
  
  const isNewUser = recoilNewUser || 0;
  const isBanned = recoilBanned || { banned: false };
  
  // Function to fetch widget settings from backend
  const fetchWidgetSettings = async () => {
    try {
      console.log("Fetching dashboard widget settings from backend...");
      const response = await api.get("/v1/user-settings", { withCredentials: true });
      console.log("Widget settings response:", response.data);
      if (response.data) {
        setWidgetSettings(response.data.dashboardWidgets);
        setWidgetOrder(response.data.widgetOrder);
        console.log("Widget settings loaded successfully");
      }
    } catch (err) {
      console.error("Failed to fetch widget settings:", err);
      console.error("Error details:", err.response?.data || err.message);
      // Use defaults if fetch fails - already set in initial state
    } finally {
      setWidgetSettingsLoading(false);
    }
  };

  // Fetch widget settings from backend on mount
  useEffect(() => {
    fetchWidgetSettings();
  }, []);

  // Listen for settings changes and refresh when window gains focus
  useEffect(() => {
    const handleFocus = () => {
      console.log("Dashboard gained focus, refreshing widget settings...");
      fetchWidgetSettings();
    };

    const handleSettingsChange = () => {
      console.log("Settings changed, refreshing widget settings...");
      fetchWidgetSettings();
    };

    // Listen for window focus to refresh settings when user comes back
    window.addEventListener('focus', handleFocus);
    
    // Listen for custom settings change event
    window.addEventListener('userSettingsChanged', handleSettingsChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('userSettingsChanged', handleSettingsChange);
    };
  }, []);

  // Fetch new user status from API on component mount
  useEffect(() => {
    const fetchNewUserStatus = async () => {
      try {
        // Check if user is logged in before making API call
        const authUser = localStorage.getItem("authUser");
        if (!authUser) {
          console.warn("Dashboard: No authUser found, redirecting to login");
          window.location.href = "/login";
          return;
        }

        await api.get("/v1/new-status", { withCredentials: true });
        setHasError(false);
      } catch (err) {
        console.error("Failed to fetch new user status:", err);
        
        // If it's an auth error, the interceptor will handle redirect
        // Otherwise, show error but still render dashboard
        if (err?.response?.status !== 401 && err?.response?.status !== 403) {
          setHasError(true);
          setErrorMessage('Unable to load user status. Some features may not work correctly.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchNewUserStatus();
  }, []);

  // Show loading indicator while fetching new user status
  // But don't block for too long - timeout after 3 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn("Dashboard loading timeout - forcing render");
        setLoading(false);
        setHasError(true);
        setErrorMessage('Dashboard loaded with limited functionality. Some features may not work correctly.');
      }
    }, 3000);
    
    return () => clearTimeout(timeout);
  }, [loading]);

  if (loading) {
    return (
      <div className="page-content">
        <Breadcrumbs title="Pokémon Radiance" breadcrumbItem="Dashboard" />
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-3">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Breadcrumbs title="Pokémon Radiance" breadcrumbItem="Dashboard" />
      
      {/* Show error message if API call failed but still render dashboard */}
      {hasError && (
        <Alert color="warning" className="mb-3">
          <small>{errorMessage}</small>
        </Alert>
      )}
      
      {/* New User Modal - safely render */}
      {isNewUser === 1 && (
        <SafeComponent 
          component={NewUserModal} 
          name="New User Modal"
          fallback={null} // Don't show error for modal, just skip it
        />
      )}
      
      {/* Banned User Alert */}
      {isBanned && isBanned.banned && (
        <Alert color="danger" className="mb-3">
          This account has been suspended. Certain features are not available.
        </Alert>
      )}
      
      {/* Render widgets in order from Settings */}
      {widgetOrder.map((widgetKey, index) => {
        // Handle special layout for weekly missions and last active users
        if ((widgetKey === 'weeklyMissions' || widgetKey === 'lastActive') && 
            (!isBanned || !isBanned.banned)) {
          
          // Check if both widgets are enabled and adjacent in the order
          const weeklyIndex = widgetOrder.indexOf('weeklyMissions');
          const lastActiveIndex = widgetOrder.indexOf('lastActive');
          const bothEnabled = widgetSettings.weeklyMissions && widgetSettings.lastActive;
          
          // If both are enabled and adjacent, render them together in a Row
          if (bothEnabled && Math.abs(weeklyIndex - lastActiveIndex) === 1) {
            // Only render on the first widget of the pair
            if (widgetKey === 'weeklyMissions' && lastActiveIndex === weeklyIndex + 1) {
              return (
                <Row key={`row-weekly-lastactive`}>
                  {renderWidget('weeklyMissions')}
                  {renderWidget('lastActive')}
                </Row>
              );
            } else if (widgetKey === 'lastActive' && weeklyIndex === lastActiveIndex + 1) {
              return (
                <Row key={`row-lastactive-weekly`}>
                  {renderWidget('lastActive')}
                  {renderWidget('weeklyMissions')}
                </Row>
              );
            } else {
              // Skip the second widget since it was already rendered
              return null;
            }
          }
          
          // If they're not adjacent or one is disabled, render individually
          return renderWidget(widgetKey);
        }
        
        // Regular widget rendering
        return renderWidget(widgetKey);
      }).filter(Boolean)}
    </div>
  );
};

export default Dashboard;
